import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const PurchasesList = () => {
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";
  const navigate = useNavigate();

  // Calculate default date range: last 30 days to today
  const getDefaultDateRange = () => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - 30); // 30 days ago
    const toDate = new Date(today);
    return {
      from_date: fromDate.toISOString().split("T")[0],
      to_date: toDate.toISOString().split("T")[0],
    };
  };

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    purchase: null,
  });

  // Date filters
  const [dateFilters, setDateFilters] = useState(getDefaultDateRange());
  const [isDefaultRange, setIsDefaultRange] = useState(true);

  useEffect(() => {
    fetchPurchases();
  }, []);

  useEffect(() => {
    if (searchText.trim() == "") {
      setFilteredPurchases(purchases);
    } else {
      const filtered = purchases.filter(
        (purchase) =>
          (purchase.supplier_name || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (purchase.purchase_remark || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          String(purchase.total_amount || "").includes(searchText)
      );
      setFilteredPurchases(filtered);
    }
  }, [searchText, purchases]);

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("grage_id", grage_id);
      formData.append("from_date", dateFilters.from_date);
      formData.append("to_date", dateFilters.to_date);
      const response = await axios.post(`${server_url}purchase.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status == "1") {
        setPurchases(response.data.result || []);
        setFilteredPurchases(response.data.result || []);
      } else if (response.data.status == "0") {
        // No data found for this date range
        setPurchases([]);
        setFilteredPurchases([]);
        // Don't show error toast for no data
        if (response.data.error !== "No data found") {
          toast.error(response.data.error || "Failed to fetch purchases");
        }
      } else {
        toast.error(response.data.error || "Failed to fetch purchases");
        setPurchases([]);
        setFilteredPurchases([]);
      }
    } catch (error) {
      console.error("Fetch purchases error:", error);
      toast.error("Unable to fetch purchases. Please try again.");
      setPurchases([]);
      setFilteredPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    fetchPurchases();
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsDefaultRange(false);
  };

  const handleResetToDefault = () => {
    const defaultRange = getDefaultDateRange();
    setDateFilters(defaultRange);
    setIsDefaultRange(true);
  };

  const handleEditPurchase = (purchase) => {
    navigate(`/edit-purchase/${purchase.purchase_id}`);
  };

  const handleAddPurchase = () => {
    navigate("/add-purchase");
  };

  const handleMarkAsReceived = async (purchaseId) => {
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("purchase_id", purchaseId);
      formData.append("purchase_status", "2"); // 2 = received
      const response = await axios.post(
        `${server_url}update_purchase.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (response.data.status == "1") {
        toast.success("Purchase marked as received!");
        // Update local state
        setPurchases((prev) =>
          prev.map((purchase) =>
            purchase.purchase_id == purchaseId
              ? { ...purchase, purchase_status: "2" }
              : purchase
          )
        );
      } else {
        toast.error(response.data.error || "Failed to update purchase");
      }
    } catch (error) {
      console.error("Mark as received error:", error);
      toast.error("Unable to update purchase. Please try again.");
    }
  };

  const openDeleteModal = (purchase) => {
    setDeleteModal({ isOpen: true, purchase });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, purchase: null });
  };

  const confirmDeletePurchase = async () => {
    const purchase = deleteModal.purchase;
    if (!purchase) return;
    setDeleteLoading(purchase.purchase_id);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("purchase_id", purchase.purchase_id);
      formData.append("action", "delete_purchase");
      formData.append("grage_id", grage_id);
      const response = await axios.post(`${server_url}purchase.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status == "1") {
        toast.success("Purchase deleted successfully!");
        setPurchases((prev) =>
          prev.filter((i) => i.purchase_id !== purchase.purchase_id)
        );
        setFilteredPurchases((prev) =>
          prev.filter((i) => i.purchase_id !== purchase.purchase_id)
        );
        closeDeleteModal();
      } else {
        toast.error(response.data.error || "Failed to delete purchase");
      }
    } catch (error) {
      console.error("Delete purchase error:", error);
      toast.error("Unable to delete purchase. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const getStatusColor = (status) => {
    if (status == "2") return "#10b981"; // Received - Green
    return "#f59e0b"; // Pending - Orange
  };

  const getStatusText = (status) => {
    if (status == "2") return "Received";
    return "Pending";
  };

  return (
    <div className="history-container">
      {/* Sticky Header Section */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            <form onSubmit={handleSearch} className="search-form">
              <div className="input-group same-line">
                <input
                  type="date"
                  name="from_date"
                  value={dateFilters.from_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="From Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  name="to_date"
                  value={dateFilters.to_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="To Date"
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>
            {/* Search Bar Below Date Filters */}
            <div style={{ marginTop: "10px" }}>
              <div className="input-group same-line">
                <input
                  type="text"
                  placeholder="Search by supplier, amount, or remark..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => e.key == "Enter" && handleSearch()}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Content Area */}
      <div className="content-area purchaseslist">
        {/* Purchases List */}
        <div className="home-vehicles">
          {loading ? (
            <div className="loading-vehicles">
              <div className="loading-spinner"></div>
              <div>Loading purchases...</div>
            </div>
          ) : filteredPurchases.length > 0 ? (
            <div className="vehicles-list home-icon">
              {filteredPurchases.map((purchase) => (
                <div
                  key={purchase.purchase_id}
                  className="vehicle-card section-home"
                  style={{
                    borderLeft:
                      "4px solid " + getStatusColor(purchase.purchase_status),
                  }}
                >
                  <div className="vehicle-header">
                    <table style={{ width: "100%", textAlign: "left" }}>
                      <tbody>
                        {/* Supplier and Status */}
                        <tr>
                          <td
                            style={{
                              width: "70%",
                              fontWeight: "bold",
                              fontSize: "16px",
                            }}
                            onClick={() => handleEditPurchase(purchase)}
                          >
                            {purchase.vendor_firm_name || ""}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              width: "30%",
                              fontWeight: "500",
                              fontSize: "13px",
                            }}
                          >
                            {formatDate(purchase.purchase_date)}
                          </td>
                        </tr>
                        {/* Total Amount */}
                        <tr>
                          <td
                            style={{
                              fontSize: "14px",
                              color: "#2c3e50",
                              padding: "4px 0",
                            }}
                            onClick={() => handleEditPurchase(purchase)}
                          >
                            <i
                              className="fas fa-money-bill"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Total Amount: ₹
                            {purchase.purchase_payable_amount || 0}
                          </td>
                          <td>
                            <button
                              onClick={() => openDeleteModal(purchase)}
                              style={{
                                color: "#dc2626",
                                border: "none",
                                background:"#ffffff",
                                fontSize: "13px",
                                cursor: "pointer",
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                float: "right",
                              }}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                        {/* Purchase Date */}

                        {/* Remark */}
                        {purchase.purchase_remark && (
                          <tr>
                            <td
                              colSpan="2"
                              style={{
                                fontSize: "13px",
                                color: "#666",
                                padding: "0px 0 4px 0",
                                fontStyle: "italic",
                              }}
                              onClick={() => handleEditPurchase(purchase)}
                            >
                              <i
                                className="fas fa-sticky-note"
                                style={{ marginRight: "8px" }}
                              ></i>
                              {purchase.purchase_remark}
                            </td>
                          </tr>
                        )}
                        {/* Action Buttons */}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">💳</div>
              <div className="no-vehicles-title">No Purchases Found</div>
              <div className="no-vehicles-text">
                {searchText
                  ? "Try adjusting your search terms"
                  : `No purchases found for the selected date range (${formatDate(
                      dateFilters.from_date
                    )} to ${formatDate(dateFilters.to_date)})`}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Floating Add Button */}
      <button
        className="floating-add-btn"
        onClick={handleAddPurchase}
        title="Add New Purchase"
      >
        <i style={{fontSize:"25px"}} className="fas fa-plus"></i>
      </button>
      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "350px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "24px", color: "#dc2626" }}
              ></i>
            </div>
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Delete Purchase
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to delete this purchase? This action cannot
              be undone.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePurchase}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {deleteLoading == deleteModal.purchase?.purchase_id ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <style jsx>{`
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
          position: relative;
        }
        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .content-area {
          padding-top: 140px; /* Increased for two rows of filters */
          padding-bottom: 80px;
        }
        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }
        .input-group.same-line {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          background: #f8f9fa;
        }
        .date-separator {
          color: #718096;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }
        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: "center";
          justify-content: "center";
        }
        .reset-btn {
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: "center";
          justify-content: "center";
        }
        .default-indicator {
          margin-top: 8px;
          text-align: center;
        }
        .default-badge {
          background: #e3f2fd;
          color: #0352a1;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: "center";
          gap: 6px;
        }
        .default-badge i {
          font-size: 14px;
        }
        .home-vehicles {
          padding: 20px 10px;
        }
        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }
        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .vehicles-list {
          display: grid;
          gap: 15px;
        }
        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
        }
        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .vehicle-header {
          width: 100%;
        }
        .vehicle-header table {
          width: 100%;
          border-collapse: collapse;
        }
        .vehicle-header td {
          padding: 4px 0;
          vertical-align: top;
        }
        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 0px 0;
        }
        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }
        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
        }
        .floating-add-btn {
          padding: 14px;
          font-size: 24px;
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: 60px;
          height: 60px;
          background: #0352a1;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: "center";
          justify-content: "center";
          font-size: 24px;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(3, 82, 161, 0.3);
          z-index: 99;
          transition: all 0.3s ease;
          border: none;
        }
        .floating-add-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(3, 82, 161, 0.4);
        }
        .floating-add-btn:active {
          transform: scale(0.95);
        }
        @media (max-width: 768px) {
          .content-area {
            padding-top: 160px; /* More space for mobile */
          }
          .home-vehicles {
            padding: 15px 8px;
          }
          .vehicle-card {
            padding: 12px;
          }
          .vehicle-header td {
            font-size: 14px;
          }
          .sticky-filter {
            padding: 6px 0;
          }
          .input-group.same-line {
            flex-wrap: wrap;
          }
          .search-input {
            min-width: 120px;
          }
          .date-separator {
            display: none;
          }
          .floating-add-btn {
            bottom: 70px;
            right: 15px;
            width: 56px;
            height: 56px;
            font-size: 22px;
          }
        }
        @media (max-width: 480px) {
          .content-area {
            padding-top: 110px;
          }
          .sticky-filter {
            padding: 4px 0;
          }
          .input-group.same-line {
            gap: 6px;
          }
          .search-input {
            font-size: 13px;
            padding: 8px 10px;
          }
          .search-btn,
          .reset-btn {
            padding: 8px 10px;
            min-width: 36px;
          }
          .floating-add-btn {
            bottom: 60px;
            right: 12px;
            width: 52px;
            height: 52px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default PurchasesList;
